import { Button, Heading, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type Document, type Stored } from "@overmap-ai/common"
import { selectProjectDocuments } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { DocumentTree, useDocumentTree } from "../../components/Trees/DocumentTree"
import { modelIconMapping, modelIconNameMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import { Colors } from "../../utils/colors"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/documents/")

export const ProjectDocumentsView = memo(() => {
	const { projectId } = routeApi.useParams()

	const navigate = useNavigate()
	const projectDocuments = useShallowEqaulSelector(selectProjectDocuments(projectId))
	const projectDocumentTree = useDocumentTree(projectDocuments)

	const handleClickDocument = useCallback(
		(document: Stored<Document>) => {
			void navigate({
				from: "/$organizationId/$projectId/documents/",
				to: "/$organizationId/$projectId/documents/$documentId",
				params: (prev) => ({
					...prev,
					documentId: document.uuid,
				}),
			})
		},
		[navigate],
	)

	const handleAddProjectDocument = useCallback(
		(parentDocument: Stored<Document> | null) => {
			const [offlineDocument, _promise] = sdk.documents.add({
				title: "",
				description: null,
				content: null,
				parent_document: parentDocument?.uuid ?? null,
				project: projectId,
				organization: null,
				icon: modelIconNameMapping.document,
				color: Colors.gray,
			})

			void navigate({
				to: "/$organizationId/$projectId/documents/$documentId",
				from: "/$organizationId/$projectId/documents/",
				params: (prev) => ({
					...prev,
					documentId: offlineDocument.uuid,
				}),
			})
		},
		[navigate, projectId],
	)

	const handleAddProjectRootDocument = useCallback(() => {
		handleAddProjectDocument(null)
	}, [handleAddProjectDocument])

	return (
		<ViewContainer maxWidth="6xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Project Documents</Heading>

					<Button variant="surface" size="sm" onClick={handleAddProjectRootDocument}>
						<LuIcon icon={modelIconMapping.add} />
						Create
					</Button>
				</div>

				<Separator size="full" />

				{projectDocuments.length > 0 ? (
					<DocumentTree
						tree={{ ...projectDocumentTree }}
						onClickDocument={handleClickDocument}
						onAddDocument={handleAddProjectDocument}
					/>
				) : (
					<Text accentColor="base" size="sm">
						No documents.
					</Text>
				)}
			</section>
		</ViewContainer>
	)
})
ProjectDocumentsView.displayName = "ProjectDocumentsView"
